/**
 * Authentication Storage Module
 * 
 * Manages persistent storage of outlet authentication state in localStorage.
 * Handles authentication persistence across app restarts.
 */

export type DeviceRole = 'customer' | 'capture' | 'cashier';

export interface StoredAuthState {
  outletId: string;
  role: DeviceRole;
  authenticatedAt: number;
  version: number; // For future migrations
  // Cashier-specific fields (only present when role is 'cashier')
  userId?: string;
  username?: string;
  fullName?: string;
}

const AUTH_STORAGE_KEY = 'photobooth_outlet_auth';
const CURRENT_VERSION = 1;

/**
 * Load authentication state from localStorage
 * @returns StoredAuthState if valid auth exists, null otherwise
 */
export function loadAuthState(): StoredAuthState | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as StoredAuthState;
    
    // Validate required fields (use typeof to handle 0 timestamp)
    if (!parsed.outletId || !parsed.role || typeof parsed.authenticatedAt !== 'number') {
      console.warn('Invalid auth state in storage, clearing');
      clearAuthState();
      return null;
    }

    // Validate role value
    const validRoles: DeviceRole[] = ['customer', 'capture', 'cashier'];
    if (!validRoles.includes(parsed.role)) {
      console.warn('Invalid role in auth state, clearing');
      clearAuthState();
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('Error loading auth state from localStorage:', error);
    // Clear corrupted data
    clearAuthState();
    return null;
  }
}

/**
 * Save authentication state to localStorage
 * @param state Authentication state to persist
 * @throws Error if storage fails (quota exceeded, storage disabled, etc.)
 */
export function saveAuthState(state: Omit<StoredAuthState, 'version'>): void {
  try {
    const stateWithVersion: StoredAuthState = {
      ...state,
      version: CURRENT_VERSION,
    };

    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(stateWithVersion));
  } catch (error) {
    // Handle quota exceeded error
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded, cannot save auth state');
      
      // Try to clear old data to free space
      try {
        clearOldStorageData();
        // Retry save after clearing
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({
          ...state,
          version: CURRENT_VERSION,
        }));
        console.info('Successfully saved auth state after clearing old data');
        return;
      } catch (retryError) {
        console.error('Failed to save auth state even after clearing old data:', retryError);
        throw new Error('Storage quota exceeded. Please clear browser data and try again.');
      }
    }
    
    // Handle storage disabled (private browsing, etc.)
    if (error instanceof DOMException && 
        (error.name === 'SecurityError' || error.name === 'InvalidAccessError')) {
      console.error('localStorage is disabled or not accessible');
      throw new Error('Browser storage is disabled. Please enable cookies and local storage.');
    }
    
    console.error('Error saving auth state to localStorage:', error);
    throw new Error('Failed to save authentication state. Please try again.');
  }
}

/**
 * Clear authentication state from localStorage
 */
export function clearAuthState(): void {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing auth state from localStorage:', error);
    // Non-critical error, continue
  }
}

/**
 * Clear old storage data to free up space
 * Removes non-essential cached data while preserving authentication
 */
function clearOldStorageData(): void {
  try {
    // Get all localStorage keys
    const keys = Object.keys(localStorage);
    
    // Remove old cached data (but keep auth state)
    const keysToRemove = keys.filter(key => 
      key !== AUTH_STORAGE_KEY && 
      (key.includes('cache') || key.includes('temp') || key.includes('old'))
    );
    
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove key ${key}:`, error);
      }
    });
    
    console.info(`Cleared ${keysToRemove.length} old storage items`);
  } catch (error) {
    console.error('Error clearing old storage data:', error);
  }
}

/**
 * Check if localStorage is available and writable
 * @returns true if localStorage is available, false otherwise
 */
export function isStorageAvailable(): boolean {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    console.warn('localStorage is not available:', error);
    return false;
  }
}

/**
 * Get storage quota information (if available)
 * @returns Storage quota info or null if not available
 */
export async function getStorageQuota(): Promise<{
  usage: number;
  quota: number;
  percentUsed: number;
} | null> {
  try {
    if (navigator.storage && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const percentUsed = quota > 0 ? (usage / quota) * 100 : 0;
      
      return { usage, quota, percentUsed };
    }
  } catch (error) {
    console.warn('Failed to get storage quota:', error);
  }
  
  return null;
}
